import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { SettingFileService } from './setting-file.service';
import { TranslateService } from '@ngx-translate/core';
import { LanguageService } from '../lang/language.service';
import { ISettings } from '../../core/interfaces';
import { DefaultSettings } from './defaultSettingsData';
import { AccountService } from '../auth/account.service';
import { environment } from 'src/environments/environment';
import { CloudServiceEnum } from '../cloud-storage/enums';
import { CerberusErrorCodesEnum } from 'src/app/utils/enums/error-codes';
import { ReadingEcho } from 'src/app/core/reading-utils/enums';

@Injectable({
  providedIn: 'root'
})
export class SettingsService {
  private settingsBuffer: ISettings = { ...DefaultSettings };
  private settingsSubject: BehaviorSubject<ISettings> = new BehaviorSubject(this.settings);
  private isSettingsSaved = false;
  private nonSavedKeys: Array<string> = [];
  private oldUserSettings: ISettings | null = null;
  private storedSettings: ISettings;

  private get savingSettings(): ISettings {
    const settings = { ...this.settingsBuffer };
    this.checkNonSavedKeys(settings);
    return settings;
  }

  get settings(): ISettings { return { ...this.settingsBuffer }; }

  constructor(
    public settingFileService: SettingFileService,
    private translate: TranslateService,
    private languageService: LanguageService,
    private accountService: AccountService,
  ) { }

  cleanOnDestroy() {
    this.settingsBuffer = { ...DefaultSettings };
    this.settingsSubject.next(this.settings);
    this.isSettingsSaved = false;
    this.nonSavedKeys = [];
    this.oldUserSettings = null;
  }

  getSettingsObservable(): Observable<ISettings> {
    return this.settingsSubject.asObservable();
  }

  getCurrentCloud(): CloudServiceEnum {
    return this.settingsSubject.getValue().cloudSettings.cloudStorage;
  }

  backupSettings() {
    this.storedSettings = this.settings;
  }

  restoreExamSettings() {
    if (this.storedSettings) {
      const newSettings = this.settings;
      newSettings.examLastRestrictionTemplate = this.storedSettings.examLastRestrictionTemplate;
      this.saveSettings(newSettings);
    }
  }

  async saveSettings(srcSettings: Partial<ISettings>, saveToFile = true): Promise<void> {
    for (const key in srcSettings) {
      if (this.settingsBuffer.hasOwnProperty(key)) {
        this.settingsBuffer[key] = srcSettings[key];
      }
    }

    this.updateNonSavedKeys(srcSettings);

    await this.updateSettingsSubject(saveToFile);
  }

  private async updateSettingsSubject(saveToFile: boolean) {
    const langUI = this.languageService.getLangByCodeSafe(this.settingsBuffer.UILanguageValue);
    const langSpeech = this.languageService.getLangByCodeSafe(this.settingsBuffer.savedLanguage);

    if (langUI && langUI.code !== this.translate.currentLang) {
      this.translate.use(langUI.code);
    }

    if (langUI && langUI.code !== this.settingsBuffer.UILanguageValue) { // validate lang settings, rewrite obsolete data
      this.settingsBuffer.UILanguageValue = langUI.code;
    }

    if (langSpeech && langSpeech.code !== this.settingsBuffer.savedLanguage) { // validate lang settings, rewrite obsolete data
      this.settingsBuffer.savedLanguage = langSpeech.code;
    }

    if (langSpeech && !langSpeech.voices.find(x => x.name === this.settingsBuffer.savedVoice)) {
      const l = langSpeech.voices.find(x => x.isDefault);
      if (l) {
        this.settingsBuffer.savedVoice = l.name;
      }
    }

    if (Array.isArray(this.settingsBuffer.typingUnit)) { // validate typingUnit settings, rewrite obsolete data
      const oldUnitArray = this.settingsBuffer.typingUnit as Array<ReadingEcho>;
      this.saveSettings({ typingUnit: oldUnitArray.length === 1 ? oldUnitArray[0] : DefaultSettings.typingUnit });
    }

    this.settingsSubject.next(this.settings);

    if (saveToFile) {
      await this.saveUserSettings();
    }
  }

  private async saveUserSettings() {
    if (this.isSettingsSaved) {
      await this.updateUserSettings();
    } else {
      await this.uploadUserSettings();
      this.isSettingsSaved = true;
    }
  }

  private async updateUserSettings() {
    try {
      await this.settingFileService.updateSettingsFile(this.savingSettings);
    } catch (err) {
      console.log(err);
      this.isSettingsSaved = false;
    }
  }

  private async uploadUserSettings() {
    await this.settingFileService.uploadSettingsFile(this.savingSettings);
  }

  async getUserSettings(): Promise<void> {
    let uploadDefaultSettings = false;
    try {
      const result = await this.settingFileService.downloadSettingsFile();
      this.isSettingsSaved = true;

      if (result) {
        this.oldUserSettings = result;
        await this.saveSettings(result, false);
      }
    } catch (err) {
      console.log(err);
      // if the errorCode is 300, then there is not settings file on cerberus.
      // when that happens, upload the default settings
      // (this.settingsBuffer is DefaultSettings when initialized)
      if (err.errorCode === CerberusErrorCodesEnum.FileNotFound) {
        uploadDefaultSettings = true;
      } else {
        this.isSettingsSaved = false;
      }
    } finally {
      this.checkDefaultLang(this.settingsBuffer);
      this.updateSettingsSubject(uploadDefaultSettings);
    }
  }

  private checkDefaultLang(settings): void {
    if (settings.savedLanguage === '' || settings.UILanguageValue === '') {
      let userLanguage = this.accountService.userLanguage.toLowerCase();

      if (!!!userLanguage) {
        userLanguage = environment.defaultLangCode;
      } else if (!this.languageService.availableLangCodesForUI.includes(userLanguage)) {
        userLanguage = environment.uiLanguageForUnsupportedLanguages;
      }

      if (settings.savedLanguage === '') {
        settings.savedLanguage = userLanguage;
        this.nonSavedKeys.push('savedLanguage');
      }

      if (settings.UILanguageValue === '') {
        settings.UILanguageValue = userLanguage;
        this.nonSavedKeys.push('UILanguageValue');
      }
    }
  }

  private checkNonSavedKeys(settings: ISettings): void {
    if (this.nonSavedKeys.length === 0) {
      return;
    }

    for (const key in this.nonSavedKeys) {
      if (settings.hasOwnProperty(key)) {
        settings[key] = this.oldUserSettings !== null ? this.oldUserSettings[key] : DefaultSettings[key];
      }
    }
  }

  private updateNonSavedKeys(updatedKeys: Partial<ISettings>): void {
    if (this.nonSavedKeys.length === 0) {
      return;
    }

    const nonSavedKeys = this.nonSavedKeys.concat();

    nonSavedKeys.forEach(key => {
      if (updatedKeys.hasOwnProperty(key)) {
        this.nonSavedKeys = this.nonSavedKeys.filter(k => k !== key);
      }
    })
  }
}
